import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AnyoComponent } from 'projects/app-core/src/app/models/AnyoComponent';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import { IAnyoError } from 'projects/app-core/src/models/errorModal';
import { forkJoin } from 'rxjs';
import {
  AnyoRules,
  LyfisMasterDataResponse,
  MasterDataResponse,
  RuleEngineFacts,
} from '../../../models/masterDataResponse';
import { TherapyPlansGridRequest } from '../../../models/therapyPlansGridRequest';
import { TherapyPlansGridResponseData } from '../../../models/therapyPlansGridResp';
import { ListenerService } from '../../../services/listener.service';
import { MasterDataConfigService } from '../../../services/master-data-config.service';
import { MasterdataService } from '../../../services/masterdata.service';
import {
  ICreateOrUpdateRuleEngine,
  RuleEngineService,
} from '../../../services/rule-engine.service';
import { TherapistService } from '../../../services/therapist.service';
import { TherapyPlansService } from '../../../services/therapy-plans.service';
import {
  AttributeDefinition,
  AttributeType,
  ConditionGroup,
  DecisionOutcomeEvent,
  FactCondition,
  LogicalGroupType,
} from '../models';
import { attributeTypeToOperators, isArrayOperator } from '../operators';

type FormFactCondition = {
  fact: string;
  operator: FactCondition['operator'];
  value: string | number | string[] | number[];
};
type FormConditionGroup = {
  groupType: LogicalGroupType;
  children: Array<FormConditionGroup | FormFactCondition>;
};

type OutputConditions = {
  all?: Array<OutputConditions | FactCondition>;
  any?: Array<OutputConditions | FactCondition>;
};
type OutputRule = {
  type: string;
  conditions: OutputConditions;
  active: boolean;
  event: DecisionOutcomeEvent;
  priority: number;
};

interface DropdownOptions {
  value: string;
  data: string;
}

@Component({
  selector: 'app-rule-engine',
  templateUrl: './rule-engine.component.html',
  styleUrls: ['./rule-engine.component.scss'],
})
export class RuleEngineComponent extends AnyoComponent implements OnChanges {
  constructor(
    private fb: FormBuilder,
    auth: AuthService,
    private masterDataConfigService: MasterDataConfigService,
    private toast: ToastrService,
    private masterDataService: MasterdataService,
    private therapyPlansService: TherapyPlansService,
    private ruleEngineService: RuleEngineService,
    private route: ActivatedRoute,
    private router: Router,
    private therapistService: TherapistService,
    private listenerService: ListenerService,
  ) {
    super(auth);
  }

  masterData: MasterDataResponse | null = null;
  lyfisMasterData: LyfisMasterDataResponse | null = null;
  therapyPlans: TherapyPlansGridResponseData[] = [];
  therapyPlanOptions: DropdownOptions[] = [];
  attributeTypeToOperators = attributeTypeToOperators;
  isArrayOperator = isArrayOperator;
  dummyAttributeNames: string[] = Array.from(
    { length: 10 },
    (_, i) => `Test-${i + 1}`,
  );
  anyoRules: DropdownOptions[] = [];
  anyoRulesFull: AnyoRules[] = [];
  ruleEngineFacts: RuleEngineFacts[] = [];
  selectedRuleId: string | null = null;
  eventCategoryOptions: DropdownOptions[] = [];
  // Cached per-rule maps to avoid heavy template computations
  factMetaByName: Record<string, RuleEngineFacts> = {};
  factOptionsByName: Record<string, DropdownOptions[]> = {};
  attributeTypeByName: Record<string, AttributeType> = {};
  dataTypeByName: Record<string, 'number' | 'string' | 'array'> = {};
  // Data readiness flags and pending rule id to avoid race conditions
  private masterDataLoaded = false;
  private lyfisDataLoaded = false;
  private pendingRuleId: string | null = null;
  // Controls from parent
  @Input() readOnly: boolean | null = null;
  @Input() ruleId: string | null = null;

  isReadOnly = false;
  isEdit = false;
  partners: DropdownOptions[] = [];
  experts: DropdownOptions[] = [];
  therapists: DropdownOptions[] = [];
  listeners: DropdownOptions[] = [];

  get isForcedReadOnly(): boolean {
    return this.readOnly === true;
  }

  get canToggleEditing(): boolean {
    return this.isEdit && !this.isForcedReadOnly;
  }

  ruleSetForm: FormGroup = this.fb.group({
    name: ['', Validators.required],
    attributes: this.fb.array([]),
    conditions: this.createGroup('all'),
    event: this.fb.group({
      type: ['', Validators.required],
      params: this.fb.array([]),
    }),
    priority: [0, [Validators.required]],
    active: [true],
  });

  get attributesFA(): FormArray {
    return this.ruleSetForm.get('attributes') as FormArray;
  }

  get attributesFGs(): FormGroup[] {
    return this.attributesFA.controls as FormGroup[];
  }

  // Root groups
  get conditionsRootFG(): FormGroup {
    return this.ruleSetForm.get('conditions') as FormGroup;
  }

  get eventRootFG(): FormGroup {
    return this.ruleSetForm.get('event') as FormGroup;
  }

  async ready() {
    if (this.user) {
      this.getMasterData();
      this.getLyfisMAsterData();
      this.fetchTherapyPlans();
      this.fetchDynamicDropdownOptions();
      this.route.paramMap.subscribe((p) => {
        const id = p.get('id');
        if (id && !this.ruleId) {
          this.isEdit = true;
          this.pendingRuleId = id;
          this.tryLoadPendingRule();
        }
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['readOnly'] && this.readOnly !== null) {
      if (this.readOnly) {
        this.disableEditing();
      } else {
        this.enableEditing();
      }
    }
    if (changes['ruleId'] && this.ruleId) {
      // When a specific rule is provided by parent, treat it as view-only context
      this.isEdit = true;
      this.isReadOnly = this.readOnly === null ? false : !!this.readOnly;
      this.pendingRuleId = this.ruleId;
      // Ensure dependencies are loaded when ruleId arrives via @Input (drawer/view mode)
      // This avoids a race where first selection happens before master data finishes loading
      if (!this.masterDataLoaded) {
        this.getMasterData();
      }
      if (!this.lyfisDataLoaded) {
        this.getLyfisMAsterData();
      }
      this.tryLoadPendingRule();
    }
  }

  private resetEditor(preserveRuleSelection: boolean): void {
    while (this.attributesFA.length > 0) {
      this.attributesFA.removeAt(0);
    }
    const root = this.conditionsRootFG;
    root.get('groupType')?.setValue('all');
    root.setControl('children', this.fb.array([]));
    this.eventRootFG.get('type')?.setValue('');
    const pfa = this.getParamsFA();
    while (pfa.length > 0) {
      pfa.removeAt(0);
    }
    this.ruleSetForm.get('priority')?.setValue(0);
    this.ruleSetForm.get('active')?.setValue(true);
    this.factMetaByName = {};
    this.factOptionsByName = {};
    this.attributeTypeByName = {};
    this.dataTypeByName = {};
    if (!preserveRuleSelection) {
      this.selectedRuleId = null;
      this.ruleSetForm.get('name')?.setValue('');
    }
  }

  loadRuleForEdit(id: string): void {
    this.ruleEngineService
      .getRule(id)
      .subscribe((rule: ICreateOrUpdateRuleEngine) => {
        const match = this.anyoRulesFull.find((r) => r.rule === rule.type);
        if (match) {
          this.selectedRuleId = match.m_id;
          this.onRuleChange(match.m_id);
        }
        type Inflatable = {
          all?: Inflatable[];
          any?: Inflatable[];
          fact?: string;
          operator?: string;
          value?: unknown;
        };
        const inflateGroup = (node: Inflatable): FormGroup => {
          if (node.all || node.any) {
            const key: 'all' | 'any' = node.all ? 'all' : 'any';
            const g = this.createGroup(key);
            const arr = this.childrenFA(g);
            (node[key] as Inflatable[]).forEach((child) => {
              arr.push(inflateGroup(child));
            });
            return g;
          }
          // Fact condition
          return this.fb.group({
            fact: [node.fact as string, Validators.required],
            operator: [node.operator as string, Validators.required],
            value: new FormControl(node.value as unknown),
          });
        };
        this.ruleSetForm.setControl(
          'conditions',
          inflateGroup(rule.conditions),
        );

        // Outcome
        const eventFG = this.eventRootFG;
        eventFG
          .get('type')
          ?.setValue(
            ((rule.event as Record<string, unknown>)['type'] as string) || '',
          );
        const paramsFA = this.getParamsFA();
        while (paramsFA.length) paramsFA.removeAt(0);
        const paramsObj =
          ((rule.event as Record<string, unknown>)['params'] as Record<
            string,
            unknown
          >) || {};
        Object.keys(paramsObj).forEach((k: string) => {
          paramsFA.push(
            this.fb.group({
              key: [{ value: k, disabled: true }],
              value: [paramsObj[k] as unknown],
            }),
          );
        });

        // Active / Priority / Name (name not in form UI but captured in payload if needed)
        this.ruleSetForm.get('active')?.setValue(!!rule.active);
        this.ruleSetForm.get('priority')?.setValue(rule.priority ?? 0);
        this.ruleSetForm.get('name')?.setValue(rule.name || '');

        // Enter read-only mode when loaded via id or parent forces view-only
        const forced = this.isForcedReadOnly;
        const hasRouteId = !!this.route.snapshot.paramMap.get('id');
        this.isReadOnly = forced || hasRouteId;
        if (this.isReadOnly) {
          this.ruleSetForm.disable({ emitEvent: false });
        } else {
          this.ruleSetForm.enable({ emitEvent: false });
        }
      });
  }

  getMasterData() {
    this.masterDataService.masterDataList().subscribe({
      next: (res) => {
        this.masterData = res;
        this.anyoRulesFull = res.anyoRules || [];
        this.anyoRules = this.anyoRulesFull.map((data) => ({
          value: data.m_id,
          data: `${data.data} (${data.rule})`,
        }));
        this.eventCategoryOptions = (res.eventCategories || []).map((ec) => ({
          value: ec.m_id,
          data: ec.data,
        }));
        this.masterDataLoaded = true;
        this.tryLoadPendingRule();
      },
      error: () => {
        this.masterDataLoaded = true;
        this.tryLoadPendingRule();
      },
    });
  }

  getLyfisMAsterData() {
    this.masterDataService.lyfisMasterDataList().subscribe({
      next: (res) => {
        this.ruleEngineFacts = res.RuleEngineFacts || [];
        this.lyfisMasterData = res;
        this.lyfisDataLoaded = true;
        this.tryLoadPendingRule();
      },
      error: () => {
        this.lyfisDataLoaded = true;
        this.tryLoadPendingRule();
      },
    });
  }

  fetchDynamicDropdownOptions() {
    forkJoin({
      partners: this.masterDataService.partnerMasterDataList(),
      experts: this.masterDataConfigService.listMasterDataByExperts(),
      therapists: this.therapistService.therapistList(),
      listeners: this.listenerService.listenerList({
        export: true,
        page: 1,
        pageSize: 10,
      }),
    }).subscribe({
      next: ({ partners, experts, therapists, listeners }) => {
        try {
          // 🧩 Partners
          this.partners = partners.map((d) => ({
            data: d.companyName!,
            value: d._id!,
          }));

          // 🧩 Experts
          this.experts = experts.map((d) => ({
            data: d.name,
            value: d.expertId,
          }));

          // 🧩 Therapists
          this.therapists = therapists.map((d) => ({
            data: `${d.firstName} ${d.lastName}`,
            value: d._id!,
          }));

          // 🧩 Listeners
          this.listeners = listeners.data.map((d) => ({
            data: d.name,
            value: d.id,
          }));
        } catch (e) {
          this.toast.error('Error processing dropdown data');
        }
      },
      error: (err) => {
        const errorBody = err.error as IAnyoError;
        this.toast.error(
          errorBody.description || 'Failed to load dropdown options',
        );
      },
    });
  }

  // Resolve dropdown options by checking both masterData and lyfisMasterData
  private toDropdownOptions(
    list:
      | Array<{
          m_id?: string;
          id?: string;
          value?: string;
          data?: string;
          name?: string;
        }>
      | undefined,
  ): DropdownOptions[] {
    if (!Array.isArray(list)) return [];
    return list
      .map((e) => ({
        value: e.m_id || e.id || e.value || '',
        data: e.data || e.name || '',
      }))
      .filter((x) => !!x.value);
  }

  private lowerCamel(key: string): string {
    if (!key) return '';
    return key.charAt(0).toLowerCase() + key.slice(1);
  }

  private getOptionsForValueKey(
    optKeyRaw: string,
    factKey: string,
  ): DropdownOptions[] {
    console.log('optKeyRaw', optKeyRaw);
    console.log('factKey', factKey);
    if (optKeyRaw === 'FromLyfisQuestions') {
      const masterData = this.lyfisMasterData?.RuleEngineFacts?.find(
        (f) => f.m_id === factKey,
      );
      return this.toDropdownOptions(
        masterData?.availableOptions
          ? masterData.availableOptions.map((o) => ({
              value: o.key,
              data: o.label,
            }))
          : [],
      );
    }
    if (optKeyRaw === 'Therapist') {
      return this.therapists;
    }
    if (optKeyRaw === 'Listener') {
      return this.listeners;
    }
    if (optKeyRaw === 'Expert') {
      return this.experts;
    }
    if (optKeyRaw === 'Partner') {
      return this.partners;
    }
    const optKey = (optKeyRaw || '').trim();
    if (!optKey) return [];
    const mdKey = this.lowerCamel(optKey);

    const masterList = (
      this.masterData as unknown as Record<
        string,
        Array<{
          m_id?: string;
          id?: string;
          value?: string;
          data?: string;
          name?: string;
        }>
      >
    )?.[mdKey];
    const lyfisExact = (
      this.lyfisMasterData as unknown as Record<
        string,
        Array<{
          m_id?: string;
          id?: string;
          value?: string;
          data?: string;
          name?: string;
        }>
      >
    )?.[optKey];
    const lyfisCamel = (
      this.lyfisMasterData as unknown as Record<
        string,
        Array<{
          m_id?: string;
          id?: string;
          value?: string;
          data?: string;
          name?: string;
        }>
      >
    )?.[mdKey];

    const combined = [
      ...this.toDropdownOptions(masterList),
      ...this.toDropdownOptions(lyfisExact),
      ...this.toDropdownOptions(lyfisCamel),
    ];
    // Deduplicate by value, keep first label occurrence
    const seen = new Set<string>();
    const dedup: DropdownOptions[] = [];
    for (const o of combined) {
      if (seen.has(o.value)) continue;
      seen.add(o.value);
      dedup.push(o);
    }
    return dedup;
  }

  fetchTherapyPlans() {
    this.pageLoading = true;
    const gridRequest: TherapyPlansGridRequest = {
      page: 1,
      pageSize: 100,
      export: false,
    };

    this.therapyPlansService.fetchTherapyPlansGrid(gridRequest).subscribe({
      next: (value) => {
        this.therapyPlans = value.data;
        this.therapyPlanOptions = this.therapyPlans
          .map((plan) => {
            const val = plan.id;
            const label = plan.planName + ' (' + plan.internalPlanName + ')';
            return { value: val, data: label };
          })
          .filter((opt) => !!opt.value);
      },
      error: (err) => {
        this.toast.error(err.message);
      },
    });
  }

  onRuleChange(ruleId: string): void {
    this.selectedRuleId = ruleId;
    const rule = this.anyoRulesFull.find((r) => r.m_id === ruleId);
    if (!rule) {
      return;
    }
    this.resetEditor(true);
    (rule.applicableFacts || []).forEach((factId) => {
      const fact = this.ruleEngineFacts.find((f) => f.m_id === factId);
      if (!fact) {
        return;
      }
      const type = this.mapDataTypeToAttributeType(fact.dataType);
      const group = this.fb.group({
        name: [fact.data, Validators.required],
        type: [type, Validators.required],
      });
      this.attributesFA.push(group);
      this.factMetaByName[fact.data] = fact;
      this.attributeTypeByName[fact.data] = type;
      const dt = (fact.dataType || '').toLowerCase();
      this.dataTypeByName[fact.data] = dt.includes('array')
        ? 'array'
        : /number|numeric|int|integer|float|double/.test(dt)
          ? 'number'
          : 'string';

      const opts = this.getOptionsForValueKey(
        fact.valueDropdownOption || '',
        fact.m_id || '',
      );
      this.factOptionsByName[fact.data] = opts;
    });

    // Pre-build outcome param keys from rule.outCome
    this.setOutcomeParamKeys(rule.outCome || []);
  }

  getMasterDataForFact(fact: RuleEngineFacts): DropdownOptions[] {
    return this.getOptionsForValueKey(
      fact.valueDropdownOption || '',
      fact.m_id || '',
    );
  }

  private setOutcomeParamKeys(keys: string[]): void {
    const paramsFA = this.getParamsFA();
    while (paramsFA.length > 0) {
      paramsFA.removeAt(0);
    }
    keys.forEach((k) => {
      paramsFA.push(
        this.fb.group({
          key: [{ value: k, disabled: true }],
          value: [this.defaultValueForParam(k)],
        }),
      );
    });
  }

  private mapDataTypeToAttributeType(dataType: string): AttributeType {
    const dt = (dataType || '').toLowerCase();
    if (dt.includes('array')) {
      return 'array';
    }
    if (
      dt === 'number' ||
      dt === 'numeric' ||
      dt === 'int' ||
      dt === 'integer' ||
      dt === 'float' ||
      dt === 'double'
    ) {
      return 'number';
    }
    return 'string';
  }

  private defaultValueForParam(key: string): unknown {
    if (key === 'eventCategoryIds') {
      return [];
    }
    return '';
  }

  addAttribute(): void {
    const group = this.fb.group({
      name: ['', Validators.required],
      type: ['string' as AttributeType, Validators.required],
    });
    this.attributesFA.push(group);
  }

  removeAttribute(index: number): void {
    this.attributesFA.removeAt(index);
  }

  addOutcomeParam(): void {
    const paramsFA = this.getParamsFA();
    paramsFA.push(
      this.fb.group({ key: ['', Validators.required], value: [''] }),
    );
  }

  removeOutcomeParam(paramIndex: number): void {
    const paramsFA = this.getParamsFA();
    paramsFA.removeAt(paramIndex);
  }

  private getParamsFA(): FormArray {
    return this.eventRootFG.get('params') as FormArray;
  }

  paramsFA(): FormArray {
    return this.eventRootFG.get('params') as FormArray;
  }

  paramsFGs(): FormGroup[] {
    return this.paramsFA().controls as FormGroup[];
  }

  createGroup(type: LogicalGroupType): FormGroup {
    return this.fb.group({
      groupType: [type, Validators.required],
      children: this.fb.array([]),
    });
  }

  addNestedGroup(groupFA: FormArray, type: LogicalGroupType = 'all'): void {
    groupFA.push(this.createGroup(type));
  }

  addFactCondition(groupFA: FormArray): void {
    const fc = this.fb.group({
      fact: ['', Validators.required],
      operator: ['', Validators.required],
      value: new FormControl(''),
    });
    groupFA.push(fc);
    const sync = () => this.syncFactValueShape(fc as FormGroup);
    fc.get('fact')?.valueChanges.subscribe(() => sync());
    sync();
  }

  removeChild(groupFA: FormArray, childIndex: number): void {
    groupFA.removeAt(childIndex);
  }

  childrenFA(group: FormGroup): FormArray {
    return group.get('children') as FormArray;
  }

  // For backward compatibility in template helpers
  conditionsFG(): FormGroup {
    return this.conditionsRootFG;
  }

  eventFG(): FormGroup {
    return this.eventRootFG;
  }

  asGroup(control: AbstractControl): FormGroup {
    return control as FormGroup;
  }

  getOperatorsForChild(child: FormGroup): string[] {
    const factName = child.get('fact')?.value as string;
    const attributes = this.attributesFA.value as AttributeDefinition[];
    const attr = attributes.find((a) => a.name === factName);
    const t = (attr?.type || 'string') as AttributeType;
    return this.attributeTypeToOperators[t];
  }

  // Normalize array inputs (comma-separated) for array operators
  normalizeValueInput(child: FormGroup): void {
    const factName = child.get('fact')?.value as string;
    const selectedAttribute = (
      this.attributesFA.value as AttributeDefinition[]
    ).find((a) => a.name === factName);
    if (!selectedAttribute) {
      return;
    }
    if (selectedAttribute.type === 'array') {
      const raw = child.get('value')?.value as string;
      const arr = (raw || '')
        .split(',')
        .map((v) => v.trim())
        .filter((v) => v.length > 0);
      child.get('value')?.setValue(arr, { emitEvent: false });
    }
  }

  private syncFactValueShape(child: FormGroup): void {
    const factName = child.get('fact')?.value as string;
    if (!factName) {
      return;
    }
    const attrType =
      this.attributeTypeByName[factName] ||
      (this.attributesFA.value as AttributeDefinition[]).find(
        (a) => a.name === factName,
      )?.type ||
      'string';
    const current = child.get('value')?.value;
    if (attrType === 'array') {
      if (!Array.isArray(current)) {
        child.get('value')?.setValue([], { emitEvent: false });
      }
    } else {
      if (Array.isArray(current)) {
        child.get('value')?.setValue('', { emitEvent: false });
      }
    }
  }

  // Fact helpers (dataType / dropdown options)
  private getFactMeta(child: FormGroup): RuleEngineFacts | undefined {
    const factName = child.get('fact')?.value as string;
    return this.ruleEngineFacts.find((f) => f.data === factName);
  }

  isNumberFact(child: FormGroup): boolean {
    const dt = (this.getFactMeta(child)?.dataType || '').toLowerCase();
    return (
      dt === 'number' ||
      dt === 'numeric' ||
      dt === 'int' ||
      dt === 'integer' ||
      dt === 'float' ||
      dt === 'double'
    );
  }

  attributeTypeForChild(child: FormGroup): AttributeType {
    const factName = child.get('fact')?.value as string;
    const attr = (this.attributesFA.value as AttributeDefinition[]).find(
      (a) => a.name === factName,
    );
    return (attr?.type || 'string') as AttributeType;
  }

  getDropdownOptionsForChild(child: FormGroup): DropdownOptions[] {
    const meta = this.getFactMeta(child);
    const optKey = (meta?.valueDropdownOption || '').trim();
    if (!optKey) {
      return [];
    }
    return this.getOptionsForValueKey(optKey, meta?.m_id || '');
  }

  private tryLoadPendingRule(): void {
    if (this.pendingRuleId && this.masterDataLoaded && this.lyfisDataLoaded) {
      const id = this.pendingRuleId;
      this.pendingRuleId = null;
      this.loadRuleForEdit(id);
    }
  }

  // Render helpers
  isGroup(control: AbstractControl): boolean {
    return control instanceof FormGroup && !!control.get('groupType');
  }

  isFact(control: AbstractControl): boolean {
    return control instanceof FormGroup && !!control.get('fact');
  }

  loading = false;

  // Serialization to the final JSON structure (per task.md)
  onSave(): void {
    this.loading = true;
    if (this.ruleSetForm.invalid) {
      this.ruleSetForm.markAllAsTouched();
      return;
    }
    const json = this.serializeRuleSet();
    const payload: ICreateOrUpdateRuleEngine = {
      id: this.route.snapshot.paramMap.get('id') || undefined,
      name:
        ((this.ruleSetForm.get('name')?.value as string) || '').trim() ||
        'RuleSet',
      priority: json.priority,
      conditions: json.conditions as unknown as Record<string, unknown>,
      active: json.active,
      event: json.event as unknown as Record<string, unknown>,
      type: json.type,
    };
    this.ruleEngineService.createOrUpdate(payload).subscribe({
      next: () => {
        this.toast.success(
          `${this.isEdit ? 'Rule updated successfully' : 'Rule created successfully'}`,
        );
        this.router.navigate(['/rule-engine']);
        this.resetEditor(false);
      },
      error: (err) => {
        this.toast.error(err.message);
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  enableEditing(): void {
    this.isReadOnly = false;
    this.ruleSetForm.enable({ emitEvent: false });
  }

  disableEditing(): void {
    this.isReadOnly = true;
    this.ruleSetForm.disable({ emitEvent: false });
  }

  private serializeRuleSet(): OutputRule {
    type AttributeFormValue = { name: string; type: AttributeType };
    type RuleSetFormValue = {
      attributes: AttributeFormValue[];
      conditions: FormConditionGroup;
      event: {
        type: string;
        params: Array<{ key: string; value: string | number | boolean }>;
      };
      priority: number;
    };

    const value = this.ruleSetForm.getRawValue() as RuleSetFormValue;
    const event = {
      type: value.event.type,
      params: (value.event.params || []).reduce<
        Record<string, string | number | boolean>
      >((acc, cur) => {
        if (
          cur.key !== undefined &&
          cur.key !== null &&
          String(cur.key).trim() !== ''
        ) {
          acc[cur.key] = cur.value;
        }
        return acc;
      }, {}),
    };

    const selectedRule = this.anyoRulesFull.find(
      (r) => r.m_id === this.selectedRuleId,
    );
    const output: OutputRule = {
      type: selectedRule?.rule || 'rule',
      conditions: this.toJsonConditions(this.serializeGroup(value.conditions)),
      active: !!this.ruleSetForm.get('active')?.value,
      event,
      priority: Number(value.priority || 0),
    };

    return output;
  }

  private serializeGroup(group: FormConditionGroup): ConditionGroup {
    const children = (group.children || []).map((child) => {
      if ('groupType' in child) {
        return this.serializeGroup(child);
      }
      const fc = child as FormFactCondition;
      return {
        fact: fc.fact,
        operator: fc.operator,
        value: fc.value,
      } as FactCondition;
    });
    return {
      groupType: group.groupType,
      children,
    };
  }

  private isConditionGroupValue(
    node: ConditionGroup | FactCondition,
  ): node is ConditionGroup {
    return (node as ConditionGroup).groupType !== undefined;
  }

  toJsonConditions(group: ConditionGroup): OutputConditions {
    const key = group.groupType;
    const arr = group.children.map((child) => {
      if (this.isConditionGroupValue(child as ConditionGroup | FactCondition)) {
        return this.toJsonConditions(child as ConditionGroup);
      }
      return child as FactCondition;
    });
    return { [key]: arr };
  }
}
